import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cpu, Lock, Database } from 'lucide-react';
import { SPRING_CONFIG } from '../../utils/constants';

/**
 * PortalLoadingScreen
 * Features:
 * - Dynamic loading messages
 * - Orbital animations
 * - Progress indicators
 * - Particle effects
 * - 3D transformations
 * - Loading stages
 */


interface PortalLoadingScreenProps {
  message?: string;
  subMessage?: string;
  progress?: number | null;
  stage?: string | null;
  onComplete?: (() => void) | null;
  minDuration?: number;
  showTips?: boolean;
}

const PortalLoadingScreen: React.FC<PortalLoadingScreenProps> = ({
  message = "Loading Profile",
  subMessage = "Accessing the permaweb...",
  progress = null,
  stage = null,
  onComplete = null,
  minDuration = 1000,
  showTips = true
}) => {
  const [currentTip, setCurrentTip] = useState(0);
  const [loadingStage, setLoadingStage] = useState(0);
  const startTime = useMemo(() => Date.now(), []);

  // Loading tips
  const loadingTips = [
    "Your data is stored permanently on Arweave",
    "amPassport uses the AO computer for processing",
    "All transactions are cryptographically signed",
    "Your profile is fully decentralized",
    "Connecting to over 200+ storage nodes",
    "Verifying your cosmic credentials"
  ];

  // Loading stages
  const stages = [
    { icon: Database, label: "Fetching Data", color: "#15FF75" },
    { icon: Cpu, label: "Processing", color: "#00D4FF" },
    { icon: Lock, label: "Verifying", color: "#8B5CF6" }
  ];

  // Rotate tips
  useEffect(() => {
    if (!showTips) return;
    
    const interval = setInterval(() => {
      setCurrentTip(prev => (prev + 1) % loadingTips.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [showTips, loadingTips.length]);

  // Update loading stage
  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingStage(prev => (prev + 1) % stages.length);
    }, 1500);

    return () => clearInterval(interval);
  }, [stages.length]);

  // Handle completion
  useEffect(() => {
    if (progress === 100 && onComplete) {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDuration - elapsed);
      
      setTimeout(() => {
        onComplete();
      }, remaining);
    }
  }, [progress, onComplete, startTime, minDuration]);

  return (
    <motion.div 
      className="fixed inset-0 z-50 bg-[#0A0E1B] flex items-center justify-center overflow-hidden"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.1 }}
      transition={{ duration: 0.8, ease: "easeInOut" }}
    >
      {/* Animated background */}
      <div className="absolute inset-0">
        {/* Radial gradient pulse */}
        <motion.div
          className="absolute inset-0"
          animate={{
            background: [
              "radial-gradient(circle at center, rgba(21,255,117,0.1) 0%, transparent 50%)",
              "radial-gradient(circle at center, rgba(21,255,117,0.2) 20%, transparent 70%)",
              "radial-gradient(circle at center, rgba(21,255,117,0.1) 0%, transparent 50%)"
            ]
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(21,255,117,0.4) 1px, transparent 1px),
              linear-gradient(90deg, rgba(21,255,117,0.4) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
        
        {/* Floating particles */}
        <FloatingParticles />
      </div>
      
      {/* Main content */}
      <div className="relative z-10">
        {/* Central loader */}
        <div className="relative">
          {/* Orbital rings */}
          <OrbitalRings />
          
          {/* Center icon container */}
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
              rotate: [0, 180, 360],
            }}
            transition={{ 
              scale: { duration: 2, repeat: Infinity },
              rotate: { duration: 8, repeat: Infinity, ease: "linear" }
            }}
            style={{
              boxShadow: `
                0 0 60px rgba(21,255,117,0.6),
                0 0 120px rgba(21,255,117,0.4),
                inset 0 0 60px rgba(21,255,117,0.2)
              `
            }}
          >
            {/* Inner rotating icon */}
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            >
              
            </motion.div>
            
            {/* Progress ring */}
            {progress !== null && (
              <svg className="absolute inset-0 w-full h-full -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="62"
                  fill="none"
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="4"
                />
                <motion.circle
                  cx="64"
                  cy="64"
                  r="62"
                  fill="none"
                  stroke="#FFFFFF"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 62}`}
                  initial={{ strokeDashoffset: 2 * Math.PI * 62 }}
                  animate={{ strokeDashoffset: 2 * Math.PI * 62 * (1 - progress / 100) }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </svg>
            )}
          </motion.div>
          
          {/* Stage indicators */}
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2">
            {stages.map((stage, index) => (
              <motion.div
                key={index}
                className={`w-2 h-2 rounded-full`}
                style={{ backgroundColor: index === loadingStage ? stage.color : `rgba(255,255,255,0.2)` }}
                animate={{ scale: index === loadingStage ? [1, 1.5, 1] : 1 }}
                transition={{ duration: 0.5 }}
              />
            ))}
          </div>
        </div>
        
        {/* Text content */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          {/* Main message */}
          <div className="text-2xl font-bold text-[#E8FDF2] mb-2">{message}</div>
          
          {/* Stage or sub message */}
          <AnimatePresence mode="wait">
            <motion.div
              key={stage || loadingStage}
              className="text-[#8AA5A2] flex items-center justify-center gap-2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {stage ? (
                <span>{stage}</span>
              ) : (
                <>
                  {React.createElement(stages[loadingStage].icon, { className: "w-4 h-4" })}
                  <span>{stages[loadingStage].label}</span>
                </>
              )}
            </motion.div>
          </AnimatePresence>
          
          {/* Progress percentage */}
          {progress !== null && (
            <motion.div
              className="mt-4 text-3xl font-bold bg-gradient-to-br from-[#E8FDF2] to-[#15FF75] bg-clip-text text-transparent"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={SPRING_CONFIG}
            >
              {Math.round(progress)}%
            </motion.div>
          )}
          
          {/* Loading dots */}
          <div className="flex justify-center gap-2 mt-6">
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                className="w-2 h-2 bg-[#15FF75] rounded-full"
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.5, 1, 0.5]
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.2
                }}
              />
            ))}
          </div>
          
          {/* Tips */}
          {showTips && (
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTip}
                className="mt-8 text-sm text-[#8AA5A2] max-w-md mx-auto"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5 }}
              >
                {loadingTips[currentTip]}
              </motion.div>
            </AnimatePresence>
          )}
        </motion.div>
      </div>
      
      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-32 h-32">
        <HexagonPattern />
      </div>
      <div className="absolute bottom-0 right-0 w-32 h-32 rotate-180">
        <HexagonPattern />
      </div>
    </motion.div>
  );
};

/**
 * OrbitalRings - Animated orbital rings around the loader
 */
const OrbitalRings = () => {
  const rings = [
    { size: 200, duration: 10, reverse: false, opacity: 0.3 },
    { size: 250, duration: 15, reverse: true, opacity: 0.2 },
    { size: 300, duration: 20, reverse: false, opacity: 0.1 },
  ];

  return (
    <>
      {rings.map((ring, index) => (
        <motion.div
          key={index}
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full border-2 pointer-events-none"
          style={{
            width: ring.size,
            height: ring.size,
            borderColor: `rgba(21,255,117,${ring.opacity})`,
            borderStyle: 'dashed',
          }}
          animate={{ rotate: ring.reverse ? -360 : 360 }}
          transition={{ duration: ring.duration, repeat: Infinity, ease: "linear" }}
        >
          {/* Orbiting particles */}
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-3 h-3 bg-[#15FF75] rounded-full"
              style={{
                top: -1.5,
                left: '50%',
                transformOrigin: `0 ${ring.size / 2}px`,
              }}
              animate={{ rotate: 360 }}
              transition={{
                duration: ring.duration / 2,
                repeat: Infinity,
                ease: "linear",
                delay: (i * ring.duration) / 6,
              }}
            />
          ))}
        </motion.div>
      ))}
    </>
  );
};

/**
 * FloatingParticles - Background floating particles
 */
const FloatingParticles = () => {
  const particles = useMemo(() => 
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 20 + 10,
      delay: Math.random() * 5,
    })), []
  );

  return (
    <>
      {particles.map(particle => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            background: `radial-gradient(circle, rgba(21,255,117,0.4) 0%, transparent 70%)`,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: "easeInOut",
          }}
        />
      ))}
    </>
  );
};

/**
 * HexagonPattern - Corner decoration pattern
 */
const HexagonPattern = () => (
  <svg width="128" height="128" viewBox="0 0 128 128" className="opacity-10">
    <defs>
      <pattern id="hexagons" width="28" height="32" patternUnits="userSpaceOnUse">
        <polygon
          points="14,0 28,8 28,24 14,32 0,24 0,8"
          fill="none"
          stroke="#15FF75"
          strokeWidth="0.5"
        />
      </pattern>
    </defs>
    <rect width="128" height="128" fill="url(#hexagons)" />
  </svg>
);

// Display names
PortalLoadingScreen.displayName = 'PortalLoadingScreen';
OrbitalRings.displayName = 'OrbitalRings';
FloatingParticles.displayName = 'FloatingParticles';
HexagonPattern.displayName = 'HexagonPattern';

export default PortalLoadingScreen;